<template>
  <div class="signin-page">
    <!-- ロゴ -->
    <div class="text-center logo-area">
      <img class="logo" src="@/assets/img/header_logo.png" alt="新学社">
    </div>

    <v-row class="ma-0" justify="center">
      <!-- 左：アカウントをすでにお持ちの方（ログイン） -->
      <v-col cols="12" md="6" align="center">
        <div class="panel">
          <h2 class="panel-heading">アカウントをすでにお持ちの方はこちら</h2>
          <p class="panel-subtitle">ご登録いただいたIDとパスワードでログインできます。</p>

          <div class="notice-area">
            <p class="mb-2">令和8年度のパスワードの有効期限は、令和9年3月31日の23:59までです。御注意ください。</p>
            <p class="notice-red mb-0">※令和8年度用のアカウントは、令和8年4月1日の0:00よりご登録いただけます。<br>令和7年度より引き続きご利用の方も、改めてアカウントの発行をお願いいたします。</p>
          </div>

          <v-card class="signin-card pa-10 elevation-10">
            <div class="text-center">
              <img class="logo" src="@/assets/img/header_logo.png" alt="新学社">
            </div>
            <v-form ref="form" @submit.prevent>
              <v-card-text class="mt-8 text-center">
                <v-text-field
                  label="Login ID"
                  name="login"
                  prepend-icon="mdi-account"
                  type="text"
                  v-model="login"
                  :disabled="!isRecommendedEnvironment"
                ></v-text-field>

                <v-text-field
                  id="password"
                  label="Password"
                  name="password"
                  prepend-icon="mdi-lock"
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  :disabled="!isRecommendedEnvironment"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPassword = !showPassword"
                ></v-text-field>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <!-- 推奨環境のみログイン可能 -->
                <template v-if="isRecommendedEnvironment">
                  <v-btn
                    rounded
                    block
                    color="secondary"
                    class="signin-btn"
                    @click="signIn"
                    type="submit"
                    :disabled="!activateSubmit"
                  >
                    Sign In
                  </v-btn>
                </template>
                <template v-else>
                  <p class="not-recommended__message">ご利用いただいているブラウザでは正しく動作しないため、WindowsはEdgeまたはChrome、MacはSafariをご利用ください</p>
                </template>
              </v-card-actions>
            </v-form>
          </v-card>

          <!-- 動作環境ページ -->
          <div class="env-message">
            ブラウザは、WindowsはEdgeまたはChrome、MacはSafariをご利用ください。<br>
            詳しい動作環境は
            <router-link
              class="env-check__link"
              to="/env_check"
              target="_blank"
            >
              こちら
            </router-link>
            をご覧ください。
          </div>
        </div>
      </v-col>

      <!-- 右：アカウントをご登録される方（登録） -->
      <v-col cols="12" md="6" align="center">
        <div class="panel">
          <h2 class="panel-heading">アカウントをご登録される方はこちら</h2>

          <v-card class="register-card pa-8 elevation-10">
            <div class="riyou-message">
              ＜ご利用に際しての注意事項＞<br>
              ご提供するコンテンツの著作権は新学社にございます。<br>
              各コンテンツはご採用年度のご採用学年でのみ利用可能です。<br>
              ダウンロード後のデータも含め、上記以外でのご利用は、<span style="font-weight: bold;">著作権法に抵触する恐れがございますので、</span>お控えください。
            </div>

            <p class="agree-text">上記に同意して</p>

            <v-btn
              block
              color="secondary"
              dark
              class="mb-3"
              @click="$router.push('/enrollment/auth')"
            >
              無料版アカウント登録
            </v-btn>
            <v-btn
              block
              dark
              class="paid-btn"
              @click="$router.push('/paid_registration')"
            >
              有料用紙印刷アカウント登録
            </v-btn>
          </v-card>

          <!-- お問い合わせ -->
          <div class="faq-area">
            <div class="d-flex align-center justify-center">
              <v-manual-card
                style="width: 144px;"
                detail="よくある質問"
                @click="openUrl('/faq.pdf')"
              >
                <template v-slot:heading>
                  <v-img
                    class="avatar-manual"
                    src="@/assets/img/f_f_business_42_s512_f_business_42_0nbg.png"
                  />
                </template>
              </v-manual-card>
              <p class="faq-text mb-0 ml-4">
                システム、問題内容、エラーなどに関するお問い合わせは「よくある質問」を事前にご確認いただいた上で、解消しない場合は、下記のボタンより弊社問い合わせ窓口からご連絡ください。
              </p>
            </div>
            <v-btn
              color="green"
              dark
              class="mt-4"
              @click="openUrl('https://www.sing.co.jp/corp/contact/')"
            >
              問い合わせフォーム
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- SSO動作確認ダイアログ -->
    <v-dialog
      v-model="ssoTestDialog"
      max-width="500px"
    >
      <v-card>
        <v-toolbar class="title">SSO動作確認</v-toolbar>
        <v-card-text class="pt-5">
          <v-text-field
            v-model="ssoUid"
            label="UID"
            hint="SSOユーザーのUID"
            persistent-hint
          ></v-text-field>
          <v-text-field
            v-model="ssoSchoolCode"
            label="学校コード"
            hint="学校コード（ユーザー作成時に使用）"
            persistent-hint
            class="mt-3"
          ></v-text-field>
          <v-select
            v-model="ssoAccountType"
            :items="['free', 'paid']"
            label="アカウントタイプ"
            class="mt-3"
          ></v-select>
          <v-divider class="mt-4 mb-2"></v-divider>
          <p class="text-caption mb-2">ライセンス情報（SSO認証時に使用）</p>
          <v-row>
            <v-col cols="6">
              <v-select
                v-model="ssoGradeId"
                :items="[{text: '中学1年', value: 7}, {text: '中学2年', value: 8}, {text: '中学3年', value: 9}]"
                item-text="text"
                item-value="value"
                label="学年"
              ></v-select>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="ssoSubjectId"
                :items="[
                  {text: '国語', value: 9},
                  {text: '数学', value: 20},
                  {text: '英語', value: 26},
                  {text: '理科', value: 24},
                  {text: '社会', value: 13},
                  {text: '技術', value: 34},
                  {text: '家庭', value: 35},
                  {text: '保健・体育', value: 30},
                ]"
                item-text="text"
                item-value="value"
                label="LMS教科ID"
              ></v-select>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="justify-space-around pb-4">
          <v-btn color="primary" @click="ssoCreateUser" :loading="ssoLoading">
            1. ユーザー作成
          </v-btn>
          <v-btn color="success" @click="ssoAuth" :loading="ssoLoading">
            2. SSO認証
          </v-btn>
          <v-btn color="error" @click="ssoDeleteUser" :loading="ssoLoading">
            3. ユーザー削除
          </v-btn>
        </v-card-actions>
        <v-card-actions class="justify-center pb-4">
          <v-btn text @click="ssoTestDialog = false">閉じる</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 社会・技術・家庭に関しては学年が合っているかを確認 -->
    <!-- https://combine.backlog.jp/view/SIN_DEV-303 -->
    <v-dialog
      v-model="isRequiredCheckGrade"
      persistent
      max-width="400px"
    >
      <v-card
        min-height="150px"
      >
        <v-toolbar
          class="title"
        >ログインするアカウントの学年確認</v-toolbar>

        <v-card-text class="px-5">
          <!-- 学年確認 -->
          <div class="py-3">
            <p class="mb-0">{{grade}}年のテスト作成で間違いないでしょうか。</p>
          </div>
        </v-card-text>

        <v-card-actions class="card-action">
          <template>
            <v-btn color="secondary" dark class="dialog-Button" @click="checkGrade(true)">はい</v-btn>
            <v-btn color="gray" class="dialog-Button" @click=checkGrade(false)>いいえ</v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import VManualCard from '@/components/molecules/ManualCard'

export default {
  components: {
    VManualCard,
  },
  computed: {
    // ログインボタンの活性非活性化
    activateSubmit() {
      return this.login !== '' && this.password !== ''
    },
    // 開発環境かどうか
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
    // 推奨環境かどうか
    isRecommendedEnvironment() {
      const userAgent = window.navigator.userAgent.toLowerCase();

      if(userAgent.indexOf('msie') != -1 || userAgent.indexOf('trident') != -1) {
        // IE
        return false
      } else if(userAgent.indexOf('edge') != -1) {
        return true
      } else if(userAgent.indexOf('chrome') != -1) {
        return true
      } else if(userAgent.indexOf('safari') != -1) {
        return true
      } else if(userAgent.indexOf('firefox') != -1) {
        return false
      } else if(userAgent.indexOf('opera') != -1) {
        return false
      } else {
        return false
      }
    }
  },
  data () {
    return {
      login: '',
      password: '',
      showPassword: false,
      grade: '',
      isRequiredCheckGrade: false,
      // SSO動作確認用
      ssoTestDialog: false,
      ssoUid: 'sso-test-user',
      ssoSchoolCode: '010101',
      ssoAccountType: 'free',
      ssoGradeId: 7,
      ssoSubjectId: 9,  // LMS教科ID: 9=国語
      ssoLoading: false,
      ssoToken: null,
    }
  },
  async mounted() {
    // ログイン中であればログアウト処理
    if (this.$store.state.user.accessToken) {
      await this.$axios.delete('/api/v1/auth/sign_out')
    }
    // ログイン中と判断される値を初期化
    this.$store.commit('user/logout', {})

    // 登録完了画面から引き継いだログイン情報があれば入力欄へ反映
    const { prefillLogin, prefillPassword } = this.$store.state.school
    if (prefillLogin) {
      this.login = prefillLogin
      this.password = prefillPassword
      // 一度使ったら破棄
      this.$store.commit('school/setPrefillCredentials', { login: null, password: null })
    }
  },
  methods: {
    // ログインAPI実行処理
    async signIn() {
      const res = await this.$axios.post('/api/v1/auth/sign_in', {
        login: this.login,
        password: this.password,
      })
      this.$app.$refs.loading.open({message: 'ログイン中です'})
      // 成功時の処理

      // レスポンスヘッダーから認証情報を取得
      const auth = {
        accessToken: res.headers['access-token'],
        client:      res.headers['client'],
        uid:         res.headers['uid'],
        userId:      res.data.data.id
      }
      // 認証情報とログイン情報をstoreに格納
      this.$store.commit('user/login', auth)
      this.$store.commit('user/setRole', { role: res.data.data.type })
      this.$store.commit('user/setLastOperatedDate', { lastOperatedDate: new Date })
      this.$store.commit('user/setUserId', { userId: res.data.data.id })
      this.$store.commit('user/setIsDemo', { isDemo: res.data.data.is_demo })
      this.$store.commit('user/setRemainingDownloadCount', { remainingDownloadCount: res.data.data.remaining_download_count })
      this.$store.commit('user/setAccountType', { accountType: res.data.data.account_type })
      // axiosのヘッダーにログイン情報設定
      const strageData = JSON.parse(sessionStorage.getItem('ShingakushaEpro'));
      this.$axios.defaults.headers.common['access-token'] = strageData.user.accessToken;
      this.$axios.defaults.headers.common['client']       = strageData.user.client;
      this.$axios.defaults.headers.common['uid']          = strageData.user.uid;
      // マスタデータ取得
      await this.$store.dispatch('master/getMasterData')
      // 先生アカウントの場合、所属学校データを取得（ナビバー表示用）
      if(this.$store.getters['user/isTeacher']) {
        const url = `api/v1/schools/${res.data.data.school_id}`
        const school = await this.$axios(url)
        this.$store.commit('user/setSchoolData', { schoolData: school.data })
        // 担当教科をリセット
        this.$store.commit('setSubjectTeachers', [])
        // 進学アカウント判定
        const subjectTeachersResponse = await this.$axios.get('/api/v1/teacher/subject_teachers')
        this.$store.commit('user/setIsShingaku', { isShingaku: subjectTeachersResponse.data.some(st => st.is_shingaku) })
      }

      // 社会・技術・家庭に関しては学年が合っているかを確認
      // https://combine.backlog.jp/view/SIN_DEV-303
      const regex = /(sh|gi|ka)([123])-.*$/;
      const match = this.login.match(regex);
      if (match) {
        this.grade = match[2]
        this.isRequiredCheckGrade = true
        this.$app.$refs.loading.close()
        return
      }

      // ログインアカウントによって遷移先を振り分ける（管理者・先生）
      this.$router.push(this.$store.getters['user/homePath'])
      this.$app.$refs.loading.close()
    },
    async checkGrade(result) {
      if (result) {
        this.$router.push(this.$store.getters['user/homePath'])
      } else {
        this.$toasted.global.error({ message: '該当学年のアカウントで再度ログインしてください。' })
        this.grade = ""
        this.isRequiredCheckGrade = false
        if (this.$store.state.user.accessToken) {
          await this.$axios.delete('/api/v1/auth/sign_out')
        }
        // ログイン中と判断される値を初期化
        this.$store.commit('user/logout', {})
      }
    },
    openUrl(url) {
      window.open(url, '_blank')
    },
    // SSO動作確認用メソッド
    async ssoGetToken() {
      const res = await this.$axios.post('/api/v1/sso/get_token', {
        payload: {
          clientId: 'STmiUkmhtxSF4KNoLUJRtWD6Tbbt5SjJ',
          clientSecret: '66a2e75ae58cc59a2b721ab029ca9f53acc41655319e42a69924f8d1ee72dd9aafb8b08c56a6a500cc6bb6d80f95cf7e2a4bc04b4a419843ba4e9759451ef44f',
        }
      })
      this.ssoToken = res.data.accessToken
      return this.ssoToken
    },
    async ssoCreateUser() {
      this.ssoLoading = true
      try {
        const token = await this.ssoGetToken()
        const res = await this.$axios.post('/api/v1/sso/users', {
          payload: {
            token: token,
            systemCode: 'lms',
            singCode: 'sing',
            verb: 'create_user',
            uid: this.ssoUid,
            schoolCode: this.ssoSchoolCode,
            accountType: this.ssoAccountType,
          }
        })
        this.$toasted.global.success({ message: `ユーザー作成成功: ID=${res.data.id}` })
      } catch (error) {
        const message = error.response?.data?.errors?.join(', ') || 'ユーザー作成に失敗しました'
        this.$toasted.global.error({ message })
      } finally {
        this.ssoLoading = false
      }
    },
    async ssoAuth() {
      this.ssoLoading = true
      try {
        const token = await this.ssoGetToken()
        // SSO認証リクエスト（リダイレクトが返る）
        const form = document.createElement('form')
        form.method = 'POST'
        form.action = `${process.env.VUE_APP_API_ENDPOINT}/api/v1/sso/auth`

        const payloadInput = document.createElement('input')
        payloadInput.type = 'hidden'
        payloadInput.name = 'payload[token]'
        payloadInput.value = token
        form.appendChild(payloadInput)

        const uidInput = document.createElement('input')
        uidInput.type = 'hidden'
        uidInput.name = 'payload[uid]'
        uidInput.value = this.ssoUid
        form.appendChild(uidInput)

        const systemCodeInput = document.createElement('input')
        systemCodeInput.type = 'hidden'
        systemCodeInput.name = 'payload[systemCode]'
        systemCodeInput.value = 'lms'
        form.appendChild(systemCodeInput)

        const singCodeInput = document.createElement('input')
        singCodeInput.type = 'hidden'
        singCodeInput.name = 'payload[singCode]'
        singCodeInput.value = 'sing'
        form.appendChild(singCodeInput)

        const verbInput = document.createElement('input')
        verbInput.type = 'hidden'
        verbInput.name = 'payload[verb]'
        verbInput.value = 'auth_user'
        form.appendChild(verbInput)

        // licenses配列を追加
        const gradeIdInput = document.createElement('input')
        gradeIdInput.type = 'hidden'
        gradeIdInput.name = 'payload[licenses][0][gradeId]'
        gradeIdInput.value = this.ssoGradeId
        form.appendChild(gradeIdInput)

        const subjectIdInput = document.createElement('input')
        subjectIdInput.type = 'hidden'
        subjectIdInput.name = 'payload[licenses][0][subjectId]'
        subjectIdInput.value = this.ssoSubjectId
        form.appendChild(subjectIdInput)

        document.body.appendChild(form)
        form.submit()
      } catch (error) {
        const message = error.response?.data?.errors?.join(', ') || 'SSO認証に失敗しました'
        this.$toasted.global.error({ message })
        this.ssoLoading = false
      }
    },
    async ssoDeleteUser() {
      this.ssoLoading = true
      try {
        const token = await this.ssoGetToken()
        await this.$axios.post('/api/v1/sso/users/delete', {
          payload: {
            token: token,
            systemCode: 'lms',
            singCode: 'sing',
            verb: 'delete_user',
            uid: this.ssoUid,
          }
        })
        this.$toasted.global.success({ message: 'ユーザー削除成功' })
      } catch (error) {
        const message = error.response?.data?.errors?.join(', ') || 'ユーザー削除に失敗しました'
        this.$toasted.global.error({ message })
      } finally {
        this.ssoLoading = false
      }
    },
  }
}
</script>

<style lang="stylus" scoped>
.signin-page
  font-size 13px

.logo-area
  margin-bottom 20px

.logo
  margin-right 18px

.panel
  max-width 480px
  margin 0 auto

.panel-heading
  position relative
  text-align left
  color #2d5aa3
  font-size 18px
  font-weight bold
  padding-left 12px
  margin-bottom 6px
  border-left 5px solid #2d5aa3

.panel-subtitle
  text-align left
  color #555
  font-size 13px
  margin-bottom 12px

.notice-area
  text-align left
  font-size 14px
  margin-bottom 20px

.notice-red
  color red
  font-weight bold

.signin-card
  height 460px
  max-width 371px
  margin 0 auto

.signin-btn
  color white !important
  margin-top 40px
.signin-btn:enabled
  background-image linear-gradient(to bottom, #10318b, #55669b 30%, #2b3555)

.not-recommended__message
  color red

.env-message
  margin-top 16px
  font-size 13px

.env-check__link
  text-decoration underline

.register-card
  max-width 440px
  margin 0 auto

.riyou-message
  display block
  text-align left
  margin 0 auto 20px
  background #f0f0f0
  border 1px solid #d5d5d5
  padding 12px
  font-size 13px

.agree-text
  text-align left
  font-size 14px
  margin-bottom 8px

// 有料用紙印刷アカウント登録ボタンのアクセント色(画面案A準拠)
.paid-btn
  color white !important
  background-color #e17cc0 !important

.faq-area
  max-width 440px
  margin 24px auto 0

.faq-text
  text-align left
  font-size 13px

.avatar-manual
  margin-top: 10px;
  max-width: 60px;
  background-color #a5a5a5
  box-shadow 1px 1px 1px 1px #d8d8d8

.title
  justify-content center
  background-color: #2a5594 !important
  color white

.card-action
  justify-content space-around

.dialog-Button
  width 100px
  height 50px
</style>
